<template>
  <div class="merge-group-detail">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="goBack">
          <i class="fa fa-arrow-left"></i> 返回
        </button>
        <div class="header-info">
          <h1>{{ groupInfo.groupName || '归并组详情' }}</h1>
          <span class="header-desc">查看归并组内的票据/银证详情</span>
        </div>
      </div>
      <div class="header-stats">
        <span class="stat-item">类型: {{ groupType === 'document' ? '票据归并组' : '银证归并组' }}</span>
        <span class="stat-item">数量: {{ items.length }}</span>
        <span class="stat-item">总金额: ¥{{ formatAmount(totalAmount) }}</span>
      </div>
    </div>

    <!-- 归并组信息卡片 -->
    <div class="group-info-card">
      <div class="card-header">
        <h3>归并组信息</h3>
        <span class="status-badge" :class="groupInfo.status">{{ getStatusText(groupInfo.status) }}</span>
      </div>
      <div class="card-content">
        <div class="info-grid">
          <div class="info-item">
            <label>组名称:</label>
            <span>{{ groupInfo.groupName }}</span>
          </div>
          <div class="info-item">
            <label>创建时间:</label>
            <span>{{ formatDate(groupInfo.createdAt) }}</span>
          </div>
          <div class="info-item">
            <label>项目数量:</label>
            <span>{{ groupInfo.itemCount }}</span>
          </div>
          <div class="info-item">
            <label>总金额:</label>
            <span>¥{{ formatAmount(groupInfo.totalAmount) }}</span>
          </div>
          <div class="info-item full-width" v-if="groupInfo.groupSummary">
            <label>组描述:</label>
            <span>{{ groupInfo.groupSummary }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 项目列表 -->
    <div class="items-section">
      <div class="section-header">
        <h3>{{ groupType === 'document' ? '票据列表' : '银证列表' }}</h3>
        <div class="section-actions">
          <input
            v-model="searchKeyword"
            placeholder="搜索..."
            class="search-input"
          />
        </div>
      </div>

      <div class="items-grid">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="item-card"
        >
          <div class="card-header">
            <div class="item-title">
              <span class="item-number">{{ getItemNumber(item) }}</span>
              <span class="item-amount">¥{{ formatAmount(item.amount) }}</span>
            </div>
            <div class="card-actions">
              <button 
                class="action-btn primary"
                @click="showItemDetail(item)"
              >
                查看详情
              </button>
            </div>
          </div>
          
          <div class="card-content">
            <div class="item-info">
              <div class="info-row">
                <label>日期:</label>
                <span>{{ formatDate(getItemDate(item)) }}</span>
              </div>
              <div class="info-row">
                <label>摘要:</label>
                <span>{{ item.summary || '无' }}</span>
              </div>
              <div class="info-row" v-if="groupType === 'document'">
                <label>开票方:</label>
                <span>{{ item.issuer || '未知' }}</span>
              </div>
              <div class="info-row" v-if="groupType === 'receipt'">
                <label>银行:</label>
                <span>{{ item.payerBank || item.payeeBank || '未知' }}</span>
              </div>
              <div class="info-row">
                <label>类型:</label>
                <span>{{ getItemType(item) }}</span>
              </div>
            </div>
            
            <!-- 图片预览缩略图 -->
            <div class="item-thumbnail" v-if="getItemImage(item)">
              <img 
                :src="getItemImage(item)" 
                :alt="getItemNumber(item)"
                @click="showImagePreview(getItemImage(item), getItemNumber(item))"
                class="thumbnail-img"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="filteredItems.length === 0" class="empty-state">
        <i class="fa fa-inbox"></i>
        <p>{{ searchKeyword ? '没有找到匹配的项目' : '暂无数据' }}</p>
      </div>
    </div>

    <!-- 图片预览浮窗 -->
    <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4>{{ currentImageTitle }}</h4>
          <button class="close-btn" @click="closeImageModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <img :src="currentImageUrl" :alt="currentImageTitle" class="preview-image" />
        </div>
        <div class="modal-footer">
          <button class="btn secondary" @click="closeImageModal">关闭</button>
          <a :href="currentImageUrl" target="_blank" class="btn primary">
            <i class="fa fa-external-link"></i> 在新窗口打开
          </a>
        </div>
      </div>
    </div>

    <!-- 项目详情浮窗 -->
    <div v-if="showDetailModal" class="detail-modal" @click="closeDetailModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4>{{ groupType === 'document' ? '票据详情' : '银证详情' }}</h4>
          <button class="close-btn" @click="closeDetailModal">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="detail-content">
            <!-- 基本信息 -->
            <div class="detail-section">
              <h5>基本信息</h5>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>编号:</label>
                  <span>{{ getItemNumber(currentItem) }}</span>
                </div>
                <div class="detail-item">
                  <label>金额:</label>
                  <span class="amount">¥{{ formatAmount(currentItem.amount) }}</span>
                </div>
                <div class="detail-item">
                  <label>日期:</label>
                  <span>{{ formatDate(getItemDate(currentItem)) }}</span>
                </div>
                <div class="detail-item">
                  <label>摘要:</label>
                  <span>{{ currentItem.summary || '无' }}</span>
                </div>
              </div>
            </div>

            <!-- 票据特有信息 -->
            <div v-if="groupType === 'document'" class="detail-section">
              <h5>票据信息</h5>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>开票方:</label>
                  <span>{{ currentItem.issuer || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>收票方:</label>
                  <span>{{ currentItem.recipient || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>发票号码:</label>
                  <span>{{ currentItem.invoiceNumber || '无' }}</span>
                </div>
                <div class="detail-item">
                  <label>票据类型:</label>
                  <span>{{ currentItem.type || '未知' }}</span>
                </div>
              </div>
            </div>

            <!-- 银证特有信息 -->
            <div v-if="groupType === 'receipt'" class="detail-section">
              <h5>银证信息</h5>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>付款方:</label>
                  <span>{{ currentItem.payerName || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>收款方:</label>
                  <span>{{ currentItem.payeeName || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>付款银行:</label>
                  <span>{{ currentItem.payerBank || '未知' }}</span>
                </div>
                <div class="detail-item">
                  <label>收款银行:</label>
                  <span>{{ currentItem.payeeBank || '未知' }}</span>
                </div>
              </div>
            </div>

            <!-- 图片预览 -->
            <div v-if="getItemImage(currentItem)" class="detail-section">
              <h5>附件图片</h5>
              <div class="image-preview">
                <img 
                  :src="getItemImage(currentItem)" 
                  :alt="getItemNumber(currentItem)"
                  @click="showImagePreview(getItemImage(currentItem), getItemNumber(currentItem))"
                  class="detail-image"
                />
              </div>
            </div>

            <!-- OCR识别信息 -->
            <div v-if="currentItem.ocrRecognitionInfo" class="detail-section">
              <h5>OCR识别信息</h5>
              <div class="ocr-info">
                <pre>{{ currentItem.ocrRecognitionInfo }}</pre>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn secondary" @click="closeDetailModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mergeManagementApi from '@/api/merge-management'

export default {
  name: 'MergeGroupDetail',
  data() {
    return {
      groupType: 'document', // 'document' 或 'receipt'
      groupId: '',
      groupInfo: {},
      items: [],
      searchKeyword: '',
      
      // 浮窗控制
      showImageModal: false,
      currentImageUrl: '',
      currentImageTitle: '',
      
      showDetailModal: false,
      currentItem: null,
      
      loading: false
    }
  },
  
  computed: {
    filteredItems() {
      if (!this.searchKeyword) {
        return this.items
      }
      const keyword = this.searchKeyword.toLowerCase()
      return this.items.filter(item => {
        const itemNumber = this.getItemNumber(item).toLowerCase()
        const summary = (item.summary || '').toLowerCase()
        const issuer = (item.issuer || '').toLowerCase()
        const payerName = (item.payerName || '').toLowerCase()
        const payeeName = (item.payeeName || '').toLowerCase()
        
        return itemNumber.includes(keyword) || 
               summary.includes(keyword) || 
               issuer.includes(keyword) ||
               payerName.includes(keyword) ||
               payeeName.includes(keyword)
      })
    },
    
    totalAmount() {
      return this.items.reduce((total, item) => total + (item.amount || 0), 0)
    }
  },
  
  created() {
    this.groupType = this.$route.params.type || 'document'
    this.groupId = this.$route.params.id
    this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      try {
        await this.loadGroupInfo()
        await this.loadGroupItems()
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$Message.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },
    
    async loadGroupInfo() {
      const api = this.groupType === 'document' 
        ? mergeManagementApi.getDocumentGroupDetail 
        : mergeManagementApi.getReceiptGroupDetail
        
      const result = await api(this.groupId)
      if (result.success) {
        this.groupInfo = result.data || {}
      } else {
        throw new Error(result.message || '获取归并组信息失败')
      }
    },
    
    async loadGroupItems() {
      const api = this.groupType === 'document'
        ? mergeManagementApi.getDocumentGroupItems
        : mergeManagementApi.getReceiptGroupItems

      const result = await api(this.groupId)
      if (result.success) {
        this.items = result.data || []
      } else {
        throw new Error(result.message || '获取归并组项目失败')
      }
    },

    // 工具方法
    goBack() {
      // 检查来源参数，如果有则返回到指定页面，否则返回到归并组管理
      const from = this.$route.query.from
      if (from === 'merge-management') {
        this.$router.push({
          path: '/merge-management',
          query: { from: 'group-detail' }
        })
      } else if (from === 'unified-query') {
        this.$router.push('/unified-query')
      } else {
        // 默认返回到归并组管理页面
        this.$router.push({
          path: '/merge-management',
          query: { from: 'group-detail' }
        })
      }
    },

    getItemNumber(item) {
      return this.groupType === 'document'
        ? (item.billNo || item.invoiceNumber || '无编号')
        : (item.receiptNo || item.serialNumber || '无编号')
    },

    getItemDate(item) {
      return this.groupType === 'document'
        ? item.billDate
        : (item.receiptsDate || item.transferDate)
    },

    getItemType(item) {
      return this.groupType === 'document'
        ? (item.type || '未知类型')
        : (item.paymentMethod || '银行转账')
    },

    getItemImage(item) {
      return this.groupType === 'document'
        ? item.attachmentPath
        : item.filePath
    },

    getStatusText(status) {
      const statusMap = {
        'ACTIVE': '活跃',
        'DISSOLVED': '已解散',
        'INACTIVE': '非活跃'
      }
      return statusMap[status] || status
    },

    // 浮窗控制
    showItemDetail(item) {
      this.currentItem = item
      this.showDetailModal = true
    },

    closeDetailModal() {
      this.showDetailModal = false
      this.currentItem = null
    },

    showImagePreview(imageUrl, title) {
      this.currentImageUrl = imageUrl
      this.currentImageTitle = title
      this.showImageModal = true
    },

    closeImageModal() {
      this.showImageModal = false
      this.currentImageUrl = ''
      this.currentImageTitle = ''
    },

    // 格式化方法
    formatAmount(amount) {
      if (!amount) return '0.00'
      return Number(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    formatDate(date) {
      if (!date) return '无'
      return new Date(date).toLocaleDateString('zh-CN')
    }
  }
}
</script>

<style scoped>
.merge-group-detail {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.back-btn {
  padding: 8px 16px;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.back-btn:hover {
  background: #e0e0e0;
}

.header-info h1 {
  margin: 0 0 5px 0;
  font-size: 24px;
  color: #2c3e50;
}

.header-desc {
  color: #7f8c8d;
  font-size: 14px;
}

.header-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  padding: 8px 16px;
  background: #ecf0f1;
  border-radius: 20px;
  font-size: 14px;
  color: #2c3e50;
}

/* 归并组信息卡片 */
.group-info-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
}

.card-header h3 {
  margin: 0;
  color: #2c3e50;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

.status-badge.ACTIVE {
  background: #d4edda;
  color: #155724;
}

.status-badge.DISSOLVED {
  background: #f8d7da;
  color: #721c24;
}

.card-content {
  padding: 20px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-item label {
  font-weight: bold;
  color: #7f8c8d;
  font-size: 14px;
}

.info-item span {
  color: #2c3e50;
}

/* 项目列表 */
.items-section {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ecf0f1;
}

.section-header h3 {
  margin: 0;
  color: #2c3e50;
}

.search-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 200px;
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  padding: 20px;
}

.item-card {
  border: 1px solid #ecf0f1;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
}

.item-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transform: translateY(-2px);
}

.item-card .card-header {
  background: #f8f9fa;
  padding: 15px;
  border-bottom: 1px solid #ecf0f1;
}

.item-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.item-number {
  font-weight: bold;
  color: #2c3e50;
}

.item-amount {
  font-weight: bold;
  color: #27ae60;
  font-size: 16px;
}

.card-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.action-btn.primary {
  background: #3498db;
  color: white;
}

.action-btn.primary:hover {
  background: #2980b9;
}

.item-card .card-content {
  padding: 15px;
  display: flex;
  gap: 15px;
}

.item-info {
  flex: 1;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
}

.info-row label {
  width: 60px;
  font-weight: bold;
  color: #7f8c8d;
  font-size: 12px;
}

.info-row span {
  color: #2c3e50;
  font-size: 12px;
}

.item-thumbnail {
  width: 80px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.thumbnail-img:hover {
  transform: scale(1.1);
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.5;
}

/* 浮窗样式 */
.image-modal, .detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 85vw;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalFadeIn 0.3s ease-out;
}

.modal-content.large {
  max-width: 1000px;
  width: 85vw;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #e8ecef;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h4 {
  margin: 0;
  color: white;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: white;
  padding: 8px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 28px;
  overflow-y: auto;
  flex: 1;
  background: #fafbfc;
}

.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #e8ecef;
  background: white;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* 图片预览 */
.preview-image {
  max-width: 100%;
  max-height: 65vh;
  object-fit: contain;
  display: block;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.image-preview {
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
}

.detail-image {
  max-width: 280px;
  max-height: 180px;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detail-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

/* 详情内容 */
.detail-content {
  max-height: 55vh;
  overflow-y: auto;
  padding-right: 8px;
}

.detail-content::-webkit-scrollbar {
  width: 6px;
}

.detail-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.detail-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.detail-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.detail-section {
  margin-bottom: 32px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.detail-section h5 {
  margin: 0 0 18px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
  position: relative;
}

.detail-section h5::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 30px;
  height: 2px;
  background: #764ba2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

.detail-item label {
  font-weight: 600;
  color: #5a6c7d;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
}

.detail-item span.amount {
  color: #27ae60;
  font-weight: bold;
  font-size: 18px;
}

.ocr-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border-left: 4px solid #667eea;
  max-height: 300px;
  overflow-y: auto;
}

.ocr-info::-webkit-scrollbar {
  width: 4px;
}

.ocr-info::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.ocr-info::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.ocr-info pre {
  margin: 0;
  white-space: pre-wrap;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 13px;
  color: #2c3e50;
  line-height: 1.6;
}

/* 按钮样式 */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn.secondary {
  background: #6c757d;
  color: white;
}

.btn.secondary:hover {
  background: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header-stats {
    flex-wrap: wrap;
  }

  .items-grid {
    grid-template-columns: 1fr;
  }

  .info-grid, .detail-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    margin: 10px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 20px);
    border-radius: 8px;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    padding: 16px 20px;
  }

  .item-card .card-content {
    flex-direction: column;
  }

  .item-thumbnail {
    width: 100%;
    height: 150px;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .detail-item {
    padding: 10px;
  }
}
</style>
</script>
